// Filter bar — status, site type and date-range selects for the hosted sites list (values live in the URL)

import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import type { DateRange, SiteUrlFilters } from './utils'

export type { DateRange } from './utils'
export type FilterBarValues = SiteUrlFilters

interface FilterBarProps extends SiteUrlFilters {
  onChange: (filters: SiteUrlFilters) => void
}

const selectClass =
  'min-h-9 cursor-pointer rounded-md border border-border bg-surface px-2.5 py-2 text-sm text-fg focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent'

export const NO_SITE_FILTERS: SiteUrlFilters = { status: '', siteType: '', dateRange: 'all' }

export function FilterBar({ status, siteType, dateRange, onChange }: FilterBarProps) {
  const t = useT()
  const isFiltered = status !== '' || siteType !== '' || dateRange !== 'all'

  function update(patch: Partial<SiteUrlFilters>) {
    onChange({ status, siteType, dateRange, ...patch })
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={status}
        onChange={(e) => update({ status: e.target.value as SiteUrlFilters['status'] })}
        aria-label={t('sites.filter.status')}
        className={selectClass}
      >
        <option value="">{t('sites.filter.allStatuses')}</option>
        <option value="ready">{t('sites.status.ready')}</option>
        <option value="processing">{t('sites.status.processing')}</option>
        <option value="failed">{t('sites.status.failed')}</option>
      </select>

      <select
        value={siteType}
        onChange={(e) => update({ siteType: e.target.value as SiteUrlFilters['siteType'] })}
        aria-label={t('sites.filter.type')}
        className={selectClass}
      >
        <option value="">{t('sites.filter.allTypes')}</option>
        <option value="html">{t('sites.type.html')}</option>
        <option value="zip">{t('sites.type.zip')}</option>
      </select>

      <select
        value={dateRange}
        onChange={(e) => update({ dateRange: e.target.value as DateRange })}
        aria-label={t('sites.filter.date')}
        className={selectClass}
      >
        <option value="all">{t('sites.filter.allTime')}</option>
        <option value="7d">{t('sites.filter.last7')}</option>
        <option value="30d">{t('sites.filter.last30')}</option>
        <option value="90d">{t('sites.filter.last90')}</option>
      </select>

      {isFiltered && (
        <button
          type="button"
          onClick={() => onChange(NO_SITE_FILTERS)}
          className={cn('min-h-9 cursor-pointer rounded-md px-2.5 py-2 text-sm text-accent-text hover:bg-surface-hover')}
        >
          {t('sites.filter.reset')}
        </button>
      )}
    </div>
  )
}
